import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ShieldCheck, ArrowLeft } from 'lucide-react';
import { resetPassword } from '../api/auth';

export const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await resetPassword(token, password);
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      const detail = axios.isAxiosError(err) ? err.response?.data?.detail : null;
      setError(typeof detail === 'string' ? detail : 'Reset failed. The link may have expired — request a new one.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: 'var(--bg)' }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 w-full max-w-sm glass-strong rounded-3xl p-10"
        style={{ boxShadow: '0 0 0 1px rgba(255,255,255,0.04) inset, 0 40px 80px rgba(0,0,0,0.6)' }}
      >
        <div className="text-center mb-8">
          <div className="w-12 h-12 rounded-full bg-gold-glow border border-gold-dim flex items-center justify-center mx-auto mb-4">
            <ShieldCheck size={22} className="text-gold" />
          </div>
          <h1 className="font-display text-xl font-bold text-cream mb-1">
            {done ? 'Password updated!' : 'Set a new password'}
          </h1>
          <p className="text-xs text-text-dim">
            {done ? 'Redirecting you to sign in...' : 'Choose a strong password (8+ characters).'}
          </p>
        </div>

        {!done && !token && (
          <p className="text-sm text-red text-center mb-4">
            This reset link is missing its token. Use the full link from your email.
          </p>
        )}

        {!done && token && (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-[11px] font-medium uppercase tracking-[0.12em] text-text-dim mb-2">
                New Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                autoFocus
                className="w-full bg-white/[0.04] border-0 border-b-[1.5px] border-white/10 px-0.5 py-2.5 text-cream text-sm outline-none transition-colors duration-200 focus:border-gold placeholder:text-text-dim"
                placeholder="At least 8 characters"
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium uppercase tracking-[0.12em] text-text-dim mb-2">
                Confirm Password
              </label>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                minLength={8}
                className="w-full bg-white/[0.04] border-0 border-b-[1.5px] border-white/10 px-0.5 py-2.5 text-cream text-sm outline-none transition-colors duration-200 focus:border-gold placeholder:text-text-dim"
                placeholder="Repeat password"
              />
            </div>

            {error && <p className="text-red text-xs text-center font-medium">{error}</p>}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 btn-gold rounded-xl text-[13px] font-semibold tracking-wide disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Reset Password'}
            </button>
          </form>
        )}

        <p className="text-center mt-6">
          <Link to="/login" className="inline-flex items-center gap-1.5 text-xs text-gold-text hover:text-gold-light font-medium transition-colors">
            <ArrowLeft size={13} />
            Back to sign in
          </Link>
        </p>
      </motion.div>
    </div>
  );
};
